import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, MapPin } from "lucide-react";
import { MaskedLines } from "@/components/motion/Reveal";
import { BUSINESS } from "@/data/content";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] w-full overflow-hidden grain bg-concrete-100">
      {/* Parallax image layer */}
      <motion.div style={{ y: yImg, scale: scaleImg }} className="absolute inset-0 z-0">
        <img
          src="/media/pool-charcoal.jpg"
          alt="Charcoal concrete pool surround by Upscale Concrete Services in Sydney"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-concrete-900/70 via-concrete-900/20 to-concrete-900/40" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: yText }}
        className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8 min-h-[100svh] flex flex-col justify-between pb-16 sm:pb-20 pt-28 sm:pt-32"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex items-center gap-3 text-white/90"
        >
          <span className="overline">Est. Sydney NSW</span>
          <span className="h-px w-10 bg-white/40" />
          <span className="overline flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" /> Ropes Crossing
          </span>
        </motion.div>

        <div>
          <h1 className="font-display font-extrabold text-white tracking-tighter leading-[1.02] text-[15vw] sm:text-[11vw] lg:text-[8.5vw]">
            <MaskedLines lines={["UPSCALE", "CONCRETE"]} delay={0.15} />
          </h1>

          <div className="mt-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="max-w-md text-white/90 text-base sm:text-lg leading-relaxed"
          >
            Driveways, decorative concrete, slabs, pool surrounds &amp; fencing —
            poured level, finished clean. <span className="text-white font-medium">{BUSINESS.tagline}</span>
          </motion.p>

          <motion.a
            href="#contact"
            data-testid="hero-enquire-btn"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="group inline-flex items-center gap-3 bg-white text-concrete-900 px-7 py-4 text-sm font-semibold tracking-wide uppercase border border-white hover:bg-transparent hover:text-white transition-colors self-start"
          >
            Get a free quote
            <ArrowDownRight className="h-5 w-5 transition-transform group-hover:translate-x-1 group-hover:translate-y-1" />
          </motion.a>
        </div>
        </div>
      </motion.div>
    </section>
  );
}
